import { TaskDefinition, TaskType } from "../types";

export interface TaskSchedule {
  start: number;
  end: number;
}

export const calculateSchedule = (
  tasks: TaskDefinition[],
  planStartTime?: number,
): Record<string, TaskSchedule> => {
  // 1. Forward Pass (AEAP) - Calculate Earliest Start/End for all tasks
  const earlySchedule: Record<string, TaskSchedule> = {};
  const memoEarly = new Map<string, number>();
  const visitingEarly = new Set<string>();

  const getEarliestEndTime = (taskId: string): number => {
    if (memoEarly.has(taskId)) return memoEarly.get(taskId)!;
    if (visitingEarly.has(taskId)) return 0; // Cycle detected

    visitingEarly.add(taskId);

    const task = tasks.find((t) => t.id === taskId);
    if (!task) {
      visitingEarly.delete(taskId);
      memoEarly.set(taskId, 0);
      return 0;
    }

    let startTime = 0;
    if (task.dependencyIds && task.dependencyIds.length > 0) {
      const depEndTimes = task.dependencyIds.map(getEarliestEndTime);
      startTime = Math.max(...depEndTimes);
    }

    let duration =
      task.type === TaskType.SECTION ? 0 : task.durationMinutes || 0;

    // Dynamic Duration Calculation for Fixed End Time
    if (
      task.durationStrategy === "FIXED_END_TIME" &&
      task.fixedEndTime &&
      task.type !== TaskType.SECTION
    ) {
      if (planStartTime) {
        const dependencyEndTimeAbsolute = planStartTime + startTime * 60 * 1000;

        const startDate = new Date(planStartTime);
        const targetDate = new Date(startDate);
        targetDate.setDate(
          startDate.getDate() + (task.fixedEndTime.daysOffset || 0),
        );

        const [hours, minutes] = task.fixedEndTime.time.split(":").map(Number);
        targetDate.setHours(hours, minutes, 0, 0);

        const targetEndTimeAbsolute = targetDate.getTime();
        const diffMs = targetEndTimeAbsolute - dependencyEndTimeAbsolute;
        duration = Math.max(0, Math.ceil(diffMs / 1000 / 60));
      } else {
        // If no plan start time is set, visualize as 0 duration
        duration = 0;
      }
    }

    const endTime = startTime + duration;

    visitingEarly.delete(taskId);
    memoEarly.set(taskId, endTime);

    earlySchedule[taskId] = { start: startTime, end: endTime };
    return endTime;
  };

  tasks.forEach((t) => getEarliestEndTime(t.id));

  // 2. Determine Project Duration
  let projectDuration = 0;
  Object.values(earlySchedule).forEach((s) => {
    projectDuration = Math.max(projectDuration, s.end);
  });

  // 3. Build Successor Map (Reverse Dependencies)
  const successorMap = new Map<string, string[]>();
  tasks.forEach((t) => {
    if (t.dependencyIds) {
      t.dependencyIds.forEach((depId) => {
        if (!successorMap.has(depId)) {
          successorMap.set(depId, []);
        }
        successorMap.get(depId)!.push(t.id);
      });
    }
  });

  // 4. Calculate Final Schedule (Handling ALAP)
  const finalSchedule: Record<string, TaskSchedule> = {};
  const memoFinalStart = new Map<string, number>();
  const visitingFinal = new Set<string>();

  const getScheduledStart = (taskId: string): number => {
    if (memoFinalStart.has(taskId)) return memoFinalStart.get(taskId)!;
    if (visitingFinal.has(taskId)) return earlySchedule[taskId]?.start || 0; // Cycle safety

    visitingFinal.add(taskId);

    const task = tasks.find((t) => t.id === taskId);
    if (!task || !earlySchedule[taskId]) {
      visitingFinal.delete(taskId);
      return 0;
    }

    // Default to AEAP if not specified
    const strategy = task.schedulingStrategy || "AEAP";

    let startTime: number;

    if (strategy === "AEAP") {
      startTime = earlySchedule[taskId].start;
    } else {
      // ALAP Logic
      const successors = successorMap.get(taskId) || [];
      let limit = projectDuration;

      if (successors.length > 0) {
        const successorStarts = successors.map((sId) => getScheduledStart(sId));
        limit = Math.min(...successorStarts);
      }

      let duration =
        task.type === TaskType.SECTION ? 0 : task.durationMinutes || 0;

      if (
        task.durationStrategy === "FIXED_END_TIME" &&
        task.fixedEndTime &&
        task.type !== TaskType.SECTION
      ) {
        const early = earlySchedule[taskId];
        duration = early ? early.end - early.start : 0;
      }

      startTime = Math.max(0, limit - duration);

      // Sanity check: Start time cannot be earlier than earliest possible start time (due to dependencies)
      startTime = Math.max(startTime, earlySchedule[taskId].start);
    }

    visitingFinal.delete(taskId);
    memoFinalStart.set(taskId, startTime);
    return startTime;
  };

  tasks.forEach((t) => {
    const start = getScheduledStart(t.id);
    let duration = t.type === TaskType.SECTION ? 0 : t.durationMinutes || 0;

    // Use calculated duration from forward pass for Fixed End Time tasks
    if (t.durationStrategy === "FIXED_END_TIME" && earlySchedule[t.id]) {
      duration = earlySchedule[t.id].end - earlySchedule[t.id].start;
    }

    finalSchedule[t.id] = { start, end: start + duration };
  });

  return finalSchedule;
};

export const calculatePlanDuration = (
  tasks: TaskDefinition[],
  planStartTime?: number,
): number => {
  const schedule = calculateSchedule(tasks, planStartTime);
  let maxDuration = 0;
  Object.values(schedule).forEach((s) => {
    maxDuration = Math.max(maxDuration, s.end);
  });
  return maxDuration;
};

export const formatDuration = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};
